import csvParse from 'csv-simple-parser';
import { useCallback, useState } from 'react';
import * as t from 'type-shift';
import { loggedInFetch } from '../../modules/authentication';

export interface AdjustedPriceData {
  leadID: string;
  lenderId: string;
  finalPrice: number;
}

const adjustedPriceConverter = t.strict<AdjustedPriceData>({
  leadID: t.string,
  lenderId: t.string,
  finalPrice: t.number
});

const adjustedPriceDatasetConverter = t.array(adjustedPriceConverter);

export const parsePriceData = (data: string): AdjustedPriceData[] => {
  data = data.trim();
  let parsedData: unknown;
  if (data.startsWith('[')) {
    // Assume this is JSON format.
    parsedData = JSON.parse(data);
  } else {
    // Assume this is CSV format.
    parsedData = csvParse(data, { header: true, infer: true });
  }
  const finalDataset = adjustedPriceDatasetConverter(parsedData);
  if (finalDataset.length === 0) {
    throw new Error('No data was provided.');
  }
  return finalDataset;
};

export interface Response {
  status: 'Sending' | 'Success' | 'Failed' | 'None';
  response?: unknown;
}

export const useAdjustLeadPrice = () => {
  const [status, setStatus] = useState<Response>({ status: 'None' });
  const send = useCallback(
    ({ data, rationale }: { data: string; rationale: string }) => {
      if (!data || !rationale) {
        setStatus({
          status: 'Failed',
          response: { message: 'Data and rationale must be provided.' }
        });
        return;
      }
      setStatus({ status: 'Sending' });
      void (async () => {
        let parsedData: AdjustedPriceData[];
        try {
          parsedData = parsePriceData(data);
        } catch (err) {
          setStatus({
            status: 'Failed',
            response: {
              message: `Data is not formatted properly: ${err instanceof Error ? err.message : err}`
            }
          });
          return;
        }
        try {
          const result = await loggedInFetch('/api/price-adjustment', {
            headers: {
              /* eslint-disable-next-line @typescript-eslint/naming-convention */
              'Content-Type': 'application/json'
            },
            method: 'POST',
            body: JSON.stringify({ data: parsedData, rationale })
          });
          setStatus({
            status: result.ok ? 'Success' : 'Failed',
            response: await result.json()
          });
        } catch (err) {
          setStatus({
            status: 'Failed',
            response: { message: err instanceof Error ? err.message : err }
          });
        }
      })();
    },
    [setStatus]
  );
  return [status, send] as const;
};
